import { format } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useClientTemplates, useClientUsage } from '../../hooks/useClientData';
import { OnboardingBanner } from '../../components/OnboardingBanner';

export function DashboardPage() {
  const { data: usage, isLoading } = useClientUsage();

  const { data: templates } = useClientTemplates();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <OnboardingBanner totalLabels={usage?.totalLabels ?? 0} />
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="text-sm font-medium text-gray-500">Total Labels (30d)</div>
          <div className="text-3xl font-bold text-gray-900">
            {usage?.totalLabels.toLocaleString() ?? 0}
          </div>
        </div>
        
        <div className="card">
          <div className="text-sm font-medium text-gray-500">Success Rate</div>
          <div className="text-3xl font-bold text-green-600">
            {usage && usage.totalLabels > 0
              ? `${((usage.successfulLabels / usage.totalLabels) * 100).toFixed(1)}%`
              : '100%'}
          </div>
        </div>
        
        <div className="card">
          <div className="text-sm font-medium text-gray-500">Active Templates</div>
          <div className="text-3xl font-bold text-gray-900">
            {templates?.length ?? 0}
          </div>
        </div>
        
        <div className="card">
          <div className="text-sm font-medium text-gray-500">Avg Latency</div>
          <div className="text-3xl font-bold text-gray-900">
            {usage?.avgLatencyMs ?? 0}ms
          </div>
        </div>
      </div>

      {/* Usage Chart */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Labels Generated (Last 30 Days)</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={usage?.byDay ?? []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(value) => format(new Date(value), 'MM/dd')}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
              />
              <Line 
                type="monotone" 
                dataKey="count" 
                stroke="#2563eb" 
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Template Usage */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Usage by Template</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-4">Template</th>
                <th className="text-left py-2 px-4">Retailer</th>
                <th className="text-left py-2 px-4">Label Type</th>
                <th className="text-right py-2 px-4">Count</th>
                <th className="text-right py-2 px-4">Avg Latency</th>
              </tr>
            </thead>
            <tbody>
              {usage?.byTemplate.map((item) => (
                <tr key={item.templateKey} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-4 font-medium">{item.templateKey}</td>
                  <td className="py-2 px-4">{item.retailer}</td>
                  <td className="py-2 px-4">
                    <span className="px-2 py-1 text-xs bg-gray-100 rounded">
                      {item.labelType}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-right">{item.count.toLocaleString()}</td>
                  <td className="py-2 px-4 text-right">{item.avgLatencyMs}ms</td>
                </tr>
              ))}
              {(!usage?.byTemplate || usage.byTemplate.length === 0) && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    No usage data yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
